import {
    Component,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ApprovedMeasurementDetailsComponent } from './details/approved-measurement-details.component';
import { FuseConfirmationService } from '@fuse/services/confirmation';
import { AlertMessageService } from 'app/core/common/alert.message.service';
import { BaseRespone } from 'app/core/models/common.response';
import { Observable, of, Subject } from 'rxjs';
import { ApprovedMeasurementsService } from './approved-measurement.service';
import { ApprovedMeasurement } from './approved-measurement.types';
import { map, startWith, take, takeUntil } from 'rxjs/operators';
import { InwardReport } from 'app/modules/admin/transactions/inwards/reports.types';
import {
    FormBuilder,
    FormGroup,
    Validators,
} from '@angular/forms';
import { SelectionModel } from '@angular/cdk/collections';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { LookupService } from 'app/core/common/services/lookup.service';
import { CommonDropDown } from 'app/core/common/types/dropdown.types';

@Component({
    selector: 'app-approved-measurement',
    templateUrl: './approved-measurement.component.html',
    styleUrls: ['./approved-measurement.component.scss'],
})
export class ApprovedMeasurementComponent implements OnInit, OnDestroy {
    @Output('calculationAreas') calculationAreas: EventEmitter<any> =
        new EventEmitter();
    @Input() report: InwardReport;
    @Input() reportId: number;
    @Input() reportType: string;
    selection = new SelectionModel<ApprovedMeasurement>(true, []);
    approvedMeasurementForm: FormGroup;
    totalArea: number = 0.0;
    totalBuiltUp: number = 0.0;
    totalSuperBuiltUp: number = 0.0;
    selectedIds: number[] = [];
    selectedApprovedMeasurement: ApprovedMeasurement;
    isAllSelectedCheckBox: boolean = false;
    options: CommonDropDown[];
    filteredOptions: Observable<CommonDropDown[]>;
    approvedMeasurementform: FormGroup;

    approvedMeasurements$: Observable<ApprovedMeasurement[]>;

    private _unsubscribeAll: Subject<any> = new Subject<any>();

    constructor(
        private _matDialog: MatDialog,
        private _fuseConfirmationService: FuseConfirmationService,
        private _approvedMeasurementService: ApprovedMeasurementsService,
        private _alertMessageService: AlertMessageService,
        private _lookupService: LookupService,
        private _formBuilder: FormBuilder,
        private approvedMeasurementService: ApprovedMeasurementsService
    ) {}

    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next();
        this._unsubscribeAll.complete();
    }

    ngOnInit(): void {
        this._approvedMeasurementService
            .getApprovedMeasurements(this.reportId)
            .subscribe((res) => {
                this.calculateApprovedMeasurementsTotal();
            });

        this.approvedMeasurements$ =
            this._approvedMeasurementService.approvedMeasurements$.pipe(
                map((data) => {
                    data?.sort((a, b) => a.orderIndex - b.orderIndex);
                    return data;
                })
            );

        this._approvedMeasurementService.approvedMeasurement$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((contact: ApprovedMeasurement) => {
                this.selectedApprovedMeasurement = contact;
            });

        this.approvedMeasurementform = this._formBuilder.group({
            id: [''],
            reportId: [this.reportId],
            type: ['', [Validators.required]],
            particular: [''],
            width: [0],
            depth: [0],
            total: [0],
        });

        this._lookupService
            .getOptionByTypeId(92, this.reportType)
            .subscribe((res) => {
                this.options = res;
                this.filteredOptions = this.approvedMeasurementform
                    .get('particular')
                    .valueChanges.pipe(
                        startWith(''),
                        map((value) => this._optionsfilter(value || ''))
                    );
            });
    }

    displayOptionsFn(option: any): string {
        return option && option.name ? option.name : '';
    }

    particularValueChange(event) {
        if (event) {
            this.filteredOptions = of(this._optionsfilter(event));
        } else {
            this.filteredOptions = of(this.options);
        }
    }

    private _optionsfilter(name: string): CommonDropDown[] {
        const filterValue = name.toString().toLowerCase();

        return this.options.filter((option) =>
            option.name.toLowerCase().includes(filterValue)
        );
    }

    new(selectedType?: string): void {
        this.approvedMeasurements$ = this.approvedMeasurements$.pipe(
            map((measurements) => [
                ...measurements,
                {
                    id: 0,
                    reportId: this.reportId,
                    type: selectedType == 'Land' ? 'Land Area' : 'Carpet Area',
                    particular: '',
                    width: 0,
                    depth: 0,
                    total: 0,
                    approvedArea: 0,
                    approvedBuiltUp: 0,
                    approvedSuperBuiltUp: 0,
                    orderIndex: measurements.length + 1,
                    selected: false,
                    isEditing: true,
                    selectedType: selectedType,
                },
            ])
        );
    }

    calculateTotal(approved: ApprovedMeasurement): void {
        if (approved.width && approved.depth) {
            approved.total = Number(
                (approved.width * approved.depth).toFixed(2)
            );
        } else {
            approved.total = 0;
        }
    }

    cancelNewRow(index: number) {
        this.approvedMeasurements$
            .pipe(
                map((measurements) => {
                    if (!measurements[index].particular?.trim()) {
                        return measurements.filter((_, i) => i !== index);
                    } else {
                        measurements[index].isEditing = false;
                        return measurements;
                    }
                })
            )
            .subscribe((updatedMeasurements) => {
                this.approvedMeasurements$ = of(updatedMeasurements); // Update the observable
            });
    }

    toggleEdit(index: number) {
        this.approvedMeasurements$ = this.approvedMeasurements$.pipe(
            map((measurements) =>
                measurements.map((item, i) =>
                    i === index ? { ...item, isEditing: !item.isEditing } : item
                )
            )
        );
    }

    save(approvedMeasurement: ApprovedMeasurement) {
        if (
            !approvedMeasurement.particular ||
            approvedMeasurement.particular.trim() === ''
        ) {
            this._alertMessageService.error('Please select particular');
            return;
        }
        if (approvedMeasurement.id !== null && approvedMeasurement.id !== 0) {
            this.approvedMeasurementService
                .updateApprovedMeasurement(approvedMeasurement)
                .subscribe((response) => {
                    if (response.isSuccess) {
                        // Show a success message
                        this._alertMessageService.success(
                            'Approved measurement updated successfully'
                        );
                        approvedMeasurement.isEditing = false;
                    } else {
                        this._alertMessageService.error(
                            response.errors.join('\n')
                        );
                    }
                });
        } else {
            this.approvedMeasurementService
                .createApprovedMeasurement(approvedMeasurement)
                .subscribe((response) => {
                    if (response.isSuccess) {
                        // Show a success message
                        this._alertMessageService.success(
                            'Approved measurement added successfully'
                        );
                        approvedMeasurement.isEditing = false;
                    } else {
                        this._alertMessageService.error(
                            response.errors.join('\n')
                        );
                    }
                });
        }
    }

    edit(approvedMeasurement: ApprovedMeasurement): void {
        this._matDialog
            .open(ApprovedMeasurementDetailsComponent, {
                autoFocus: false,
                data: {
                    approvedMeasurement: approvedMeasurement,
                    reportType: this.reportType,
                },
            })
            .afterClosed()
            .subscribe((response: BaseRespone<ApprovedMeasurement>) => {});
    }

    /**
     * Delete the selected product using the form data
     */
    delete(id: number): void {
        // Open the confirmation dialog
        const confirmation = this._fuseConfirmationService.open({
            title: 'Delete Approved Measurement',
            message:
                'Are you sure you want to remove? This action cannot be undone!',
            actions: {
                confirm: {
                    label: 'Delete',
                },
            },
        });

        // Subscribe to the confirmation dialog closed action
        confirmation.afterClosed().subscribe((result) => {
            // If the confirm button pressed...
            if (result === 'confirmed') {
                //Delete the product on the server
                this._approvedMeasurementService
                    .deleteApprovedMeasurement(id)
                    .subscribe((res) => {
                        if (res.isSuccess) {
                            this._alertMessageService.success(
                                'Approved measurement deleted successfully'
                            );
                        } else {
                            this._alertMessageService.error(
                                res.errors.join('\n')
                            );
                        }
                    });
            }
        });
    }

    async deleteSelectedRows() {
        const confirmation = this._fuseConfirmationService.open({
            title: 'Delete All Approved Measurement',
            message:
                'Are you sure you want to remove? This action cannot be undone!',
            actions: {
                confirm: {
                    label: 'Delete',
                },
            },
        });

        const result = await confirmation.afterClosed().toPromise();

        if (result === 'confirmed') {
            const ids = this.selection.selected.map((item) => item.id);
            const res = await this._approvedMeasurementService
                .deleteAllApprovedMeasurement(ids)
                .toPromise();

            if (res.isSuccess) {
                this._alertMessageService.success(
                    'Approved measurement all deleted successfully'
                );
                this.selection.clear();
                this.approvedMeasurements$.subscribe();
            } else {
                this._alertMessageService.error(res.errors.join('\n'));
            }
        }
    }

    /**
     * Track by function for ngFor loops
     *
     * @param index
     * @param item
     */
    trackByFn(index: number, item: any): any {
        return item.id || index;
    }

    calculateApprovedMeasurementsTotal(): void {
        this._approvedMeasurementService.approvedMeasurements$.subscribe(
            (res) => {
                this.report.approvedArea = Number(
                    res
                        .filter(
                            (a) =>
                                a.type === 'Land Area' ||
                                a.type === 'Carpet Area'
                        )
                        .reduce((sum, current) => sum + current.total, 0)
                        .toFixed(2)
                );

                this.report.approvedBuiltUp = Number(
                    res
                        .filter(
                            (a) =>
                                a.type !== 'Land Area' &&
                                a.type !== 'Carpet Area'
                        )
                        .reduce((sum, current) => sum + current.total, 0)
                        .toFixed(2)
                );

                this.calculationAreas.emit();
            }
        );
    }

    updateCheckedList(event, row) {
        this.approvedMeasurements$.pipe(take(1)).subscribe((measurements) => {
            measurements.forEach((item) => {
                if (item.id == row.id) {
                    item.selected = event.checked;
                    if (item.selected) {
                        this.selection.select(item); // Select item in selection model
                    } else {
                        this.selection.deselect(item);
                    }
                }
            });

            this.isAllSelectedCheckBox = !measurements.some((a) => !a.selected);
        });
    }

    selectAll(event: any) {
        const checked = event.checked;
        this.approvedMeasurements$
            .pipe(take(1))
            .subscribe((measurements: any[]) => {
                measurements.forEach((measurement) => {
                    measurement.selected = checked;
                });
            });
    }
    checkboxLabel() {
        if (this.isAllSelected()) {
            return 'Deselect all';
        }
        if (this.selection.isEmpty()) {
            return 'Select all';
        }
        return `${this.selection.selected.length} selected`;
    }

    isAllSelected() {
        // Check if all measurements are selected
        return this.approvedMeasurements$.pipe(
            take(1),
            map((measurements) =>
                measurements.every((measurement) => measurement.selected)
            )
        );
    }

    toggleAllRows() {
        this.isAllSelected().subscribe((allSelected) => {
            if (!allSelected) {
                // Select all items
                this.approvedMeasurements$
                    .pipe(take(1))
                    .subscribe((measurements) => {
                        measurements.forEach((item) => {
                            item.selected = true;
                            this.selection.select(item); // Select item in selection model
                        });
                    });
                this.isAllSelectedCheckBox = true;
            } else {
                // Deselect all items
                this.approvedMeasurements$
                    .pipe(take(1))
                    .subscribe((measurements) => {
                        measurements.forEach((item) => {
                            item.selected = false;
                            this.selection.deselect(item); // Deselect item in selection model
                        });
                    });
                this.isAllSelectedCheckBox = false;
            }
        });
    }

    drop(event: CdkDragDrop<ApprovedMeasurement[]>): void {
        // Retrieve current data from the observable
        this.approvedMeasurements$.pipe(take(1)).subscribe((data) => {
            if (data) {
                // Reorder locally
                moveItemInArray(data, event.previousIndex, event.currentIndex);

                // Update orderIndex
                data.forEach((item, index) => {
                    item.orderIndex = index + 1; // Reset order index
                    this._approvedMeasurementService
                        .updateApprovedMeasurement(item)
                        .subscribe();
                });
            }
        });
    }

    oldPopup(selectedValue?: string) {
        let orderIndex = 0;
        this.approvedMeasurements$.subscribe((result) => {
            orderIndex = result.length + 1;
        });

        this._matDialog
            .open(ApprovedMeasurementDetailsComponent, {
                autoFocus: false,
                data: {
                    approvedMeasurement: {
                        orderIndex: orderIndex,
                        type: '',
                        particular: '',
                        width: '',
                        depth: '',
                        total: '',
                        reportId: this.reportId,
                    },
                    reportType: this.reportType,
                    selectedType: selectedValue,
                },
            })
            .afterClosed()
            .subscribe((response) => {});
    }
}
